export interface SavedAccount {
  name: string;
  email: string;
  photoUrl?: string | null;
  lastUsedAt: number;
}

export const SAVED_ACCOUNTS_STORAGE_KEY = "portal_saved_accounts";
export const MAX_SAVED_ACCOUNTS = 5;

/**
 * Safely access localStorage (returns null if window/storage is unavailable or throws)
 */
function getStorage(storageOverride?: Storage): Storage | null {
  if (storageOverride) return storageOverride;
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Normalizes email by trimming and lowercasing.
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Reads all saved accounts from localStorage, sorted by lastUsedAt descending.
 */
export function getSavedAccounts(storageOverride?: Storage): SavedAccount[] {
  const storage = getStorage(storageOverride);
  if (!storage) return [];

  try {
    const raw = storage.getItem(SAVED_ACCOUNTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    const valid: SavedAccount[] = [];
    for (const item of parsed) {
      if (
        item &&
        typeof item === "object" &&
        typeof item.email === "string" &&
        item.email.trim().length > 0
      ) {
        valid.push({
          name: typeof item.name === "string" && item.name.trim().length > 0 ? item.name.trim() : item.email.trim(),
          email: normalizeEmail(item.email),
          photoUrl: typeof item.photoUrl === "string" ? item.photoUrl : null,
          lastUsedAt: typeof item.lastUsedAt === "number" ? item.lastUsedAt : 0,
        });
      }
    }

    return valid.sort((a, b) => b.lastUsedAt - a.lastUsedAt);
  } catch {
    return [];
  }
}

/**
 * Saves or updates an account in localStorage.
 * - Deduplicates by email (case-insensitive)
 * - Updates name, photoUrl (if provided), and sets lastUsedAt to current timestamp
 * - Keeps at most MAX_SAVED_ACCOUNTS (5) most recently used accounts
 * - Never stores tokens
 * Returns the updated list of saved accounts.
 */
export function saveAccount(
  account: { name: string; email: string; photoUrl?: string | null },
  storageOverride?: Storage,
  now = Date.now(),
): SavedAccount[] {
  if (!account?.email || !account.email.trim()) {
    return getSavedAccounts(storageOverride);
  }

  const email = normalizeEmail(account.email);
  const name = account.name?.trim() || account.email.trim();
  const photoUrl = account.photoUrl !== undefined ? account.photoUrl : null;

  const current = getSavedAccounts(storageOverride);

  // Filter out existing account with same email
  const existing = current.find((a) => a.email === email);
  const remaining = current.filter((a) => a.email !== email);

  const updatedEntry: SavedAccount = {
    name,
    email,
    photoUrl: photoUrl !== null ? photoUrl : (existing?.photoUrl ?? null),
    lastUsedAt: now,
  };

  // Prepend the new/updated entry and limit to 5
  const updated = [updatedEntry, ...remaining].slice(0, MAX_SAVED_ACCOUNTS);

  const storage = getStorage(storageOverride);
  if (storage) {
    try {
      storage.setItem(SAVED_ACCOUNTS_STORAGE_KEY, JSON.stringify(updated));
    } catch {
      // Ignore storage errors (quota, disabled, etc.)
    }
  }

  return updated;
}

/**
 * Removes an account by email from localStorage.
 * Returns the updated list of saved accounts.
 */
export function removeSavedAccount(email: string, storageOverride?: Storage): SavedAccount[] {
  if (!email) return getSavedAccounts(storageOverride);

  const target = normalizeEmail(email);
  const current = getSavedAccounts(storageOverride);
  const updated = current.filter((a) => a.email !== target);

  const storage = getStorage(storageOverride);
  if (storage) {
    try {
      storage.setItem(SAVED_ACCOUNTS_STORAGE_KEY, JSON.stringify(updated));
    } catch {
      // Ignore storage errors
    }
  }

  return updated;
}

/**
 * Clears all saved accounts from localStorage.
 */
export function clearSavedAccounts(storageOverride?: Storage): void {
  const storage = getStorage(storageOverride);
  if (storage) {
    try {
      storage.removeItem(SAVED_ACCOUNTS_STORAGE_KEY);
    } catch {
      // Ignore storage errors
    }
  }
}
